import { query, Unsubscribe, where } from 'firebase/firestore';
import { useMemo } from 'react';
import { combineLatest, switchMap, tap } from 'rxjs';
import { useShallow } from 'zustand/shallow';
import { useAuthContext } from '../contexts/auth.context';
import { Entity, New } from '../dto/entity';
import { Partner, PartnerCollection } from '../dto/partner';
import {
  Partnership,
  PartnershipCollection,
  PartnershipRequest,
  PartnershipRequestConverter,
  PartnerShipStatus,
} from '../dto/partnership';
import { User, UserCollection, UserMailCollection } from '../dto/user';
import { useDataStore } from '../stores/data.store';
import { useFirebase } from './firebase.hook';

export interface PartnershipInterface {
  loadPartnerships(user: User): Unsubscribe;
  resetPartnerships(): void;
  partnershipsLoading: boolean;
  partnerships: Partnership[];

  searchUser: (mail: string) => Promise<User | undefined>;
  addPartnership: (partner: User) => Promise<PartnershipRequest | undefined>;
  acceptPartnership: (partnerShip: Partnership) => Promise<Partnership | undefined>;
  rejectPartnership: (partnerShip: Partnership) => Promise<Partnership | undefined>;
  endPartnership: (partnership: Partnership) => Promise<Partnership | undefined>;
  deletePartnership: (partnership: Partnership) => Promise<void>;
  deletePartnerships: () => Promise<void>;
}

export function usePartnership(): PartnershipInterface {
  const { user } = useAuthContext();
  const { getDocument, setDocument, deleteDocument, collection, queryToObservable, deleteQuery } = useFirebase();

  const [storePartnerships, setStorePartnerships] = useDataStore(
    useShallow((s) => [s.partnerships, s.setPartnerships]),
  );

  // DB methods
  function loadPartnerships(user: User): Unsubscribe {
    // partners
    const [unsubscribePartners, partners$] = queryToObservable<Partner>(
      collection(UserCollection, user.id, PartnerCollection),
    );

    // partnerships
    const [unsubscribeRequesterPartnerships, requesterPartnerships$] = queryToObservable<PartnershipRequest>(
      query(
        collection(PartnershipCollection).withConverter(PartnershipRequestConverter),
        where('requesterId', '==', user.id),
      ),
    );
    const [unsubscribeResponderPartnerships, responderPartnerships$] = queryToObservable<PartnershipRequest>(
      query(
        collection(PartnershipCollection).withConverter(PartnershipRequestConverter),
        where('responderId', '==', user.id),
      ),
    );
    const partnerships$ = combineLatest([requesterPartnerships$, responderPartnerships$]).pipe(
      switchMap((l) => Promise.all(l.flat().map((p) => mapPartnership(user, p)))),
      tap((l) =>
        setStorePartnerships(
          l
            .filter(
              (p) =>
                p.partner &&
                (![PartnerShipStatus.REJECTED, PartnerShipStatus.ENDED].includes(p.status) || p.enderId === user.id),
            )
            .toSorted((a, b) => (a.createdAt > b.createdAt ? 1 : -1)),
        ),
      ),
    );

    // combine and update
    combineLatest([partners$, partnerships$]).subscribe(([partners, partnerships]) =>
      updatePartnerEntries(user, partners, partnerships),
    );

    return () => {
      unsubscribePartners();
      unsubscribeRequesterPartnerships();
      unsubscribeResponderPartnerships();
    };
  }

  function resetPartnerships() {
    setStorePartnerships(undefined);
  }

  // user methods
  async function searchUser(mail: string): Promise<User | undefined> {
    if (!user) return;
    if (mail === user.mail) throw new Error('Invalid partner');

    return getDocument<Entity>(UserMailCollection, mail).then(
      (res) => res && getDocument<User>(UserCollection, res.id),
    );
  }

  async function addPartnership(partner: User): Promise<PartnershipRequest | undefined> {
    if (!user) return;
    if (partner.id == user.id) throw new Error('Invalid partner');
    if (storePartnerships?.some((p) => p.partner.id === partner.id)) throw new Error('Partnership already exists');

    const partnership: New<PartnershipRequest> = {
      requesterId: user.id,
      responderId: partner.id,
      status: PartnerShipStatus.PENDING,
      createdAt: new Date(),
    };

    return setDocument(PartnershipCollection, partnership);
  }

  async function acceptPartnership(partnerShip: Partnership): Promise<Partnership | undefined> {
    if (!user) return;

    return updateStatus(partnerShip, PartnerShipStatus.ACCEPTED);
  }

  async function rejectPartnership(partnerShip: Partnership): Promise<Partnership | undefined> {
    return updateStatus(partnerShip, PartnerShipStatus.REJECTED);
  }

  async function endPartnership(partnership: Partnership): Promise<Partnership | undefined> {
    return updateStatus(partnership, PartnerShipStatus.ENDED);
  }

  async function deletePartnership(partnership: Partnership): Promise<void> {
    if (!user) return;

    await deleteDocument(PartnershipCollection, partnership.id);
  }

  async function deletePartnerships(): Promise<void> {
    if (!user) return;

    await deleteQuery(collection(UserCollection, user.id, PartnerCollection));

    for (const partnership of storePartnerships ?? []) {
      switch (partnership.status) {
        case PartnerShipStatus.PENDING:
        case PartnerShipStatus.ACCEPTED:
          await endPartnership(partnership).then(() => deletePartnership(partnership));
          break;

        case PartnerShipStatus.REJECTED:
        case PartnerShipStatus.ENDED:
          if (partnership.enderId === user.id) await deletePartnership(partnership);
          break;
      }
    }
  }

  // helper methods
  async function updateStatus(partnerShip: Partnership, status: PartnerShipStatus): Promise<Partnership | undefined> {
    if (!user || (partnerShip.isMyRequest && status !== PartnerShipStatus.ENDED)) return;

    const update: PartnershipRequest = {
      id: partnerShip.id,
      status: status,
      createdAt: partnerShip.createdAt,
      requesterId: partnerShip.isMyRequest ? user.id : partnerShip.partner.id,
      responderId: partnerShip.isMyRequest ? partnerShip.partner.id : user.id,
    };
    if ([PartnerShipStatus.REJECTED, PartnerShipStatus.ENDED].includes(status)) update.enderId = partnerShip.partner.id;

    const updatedPartnership = await setDocument(PartnershipCollection, update, partnerShip.id);

    return {
      ...partnerShip,
      status: updatedPartnership.status,
      enderId: updatedPartnership.enderId,
    };
  }

  async function updatePartnerEntries(user: User, partners: Partner[], allPartnerships: Partnership[]): Promise<void> {
    const partnerships = allPartnerships.filter(
      (p) => p.status === PartnerShipStatus.ACCEPTED || (p.status === PartnerShipStatus.PENDING && p.isMyRequest),
    );

    const partnerMap = new Map(partners.map((p) => [p.id, p]));
    const partnerShipMap = new Map(partnerships.map((p) => [p.partner.id, p]));

    // add missing partners
    for (const { partner } of partnerships) {
      if (!partnerMap.has(partner.id)) {
        const newPartner: Partner = { id: partner.id };
        await setDocument(`${UserCollection}/${user.id}/${PartnerCollection}`, newPartner, partner.id);
      }
    }

    // remove extra partners
    for (const partner of partners) {
      if (!partnerShipMap.has(partner.id)) {
        await deleteDocument(`${UserCollection}/${user.id}/${PartnerCollection}`, partner.id);
      }
    }
  }

  async function mapPartnership(user: User, request: PartnershipRequest): Promise<Partnership> {
    const isMyRequest = request.requesterId === user.id;
    const partnerId = isMyRequest ? request.responderId : request.requesterId;

    return {
      id: request.id,
      status: request.status,
      createdAt: request.createdAt,
      isMyRequest: isMyRequest,
      partner: await getDocument<User>(UserCollection, partnerId),
      enderId: request.enderId,
    };
  }

  return useMemo(
    () => ({
      loadPartnerships,
      resetPartnerships,
      partnershipsLoading: user != null && !storePartnerships,
      partnerships: storePartnerships ?? [],
      searchUser,
      addPartnership,
      acceptPartnership,
      rejectPartnership,
      endPartnership,
      deletePartnership,
      deletePartnerships,
    }),
    [
      storePartnerships,
      setStorePartnerships,
      user,
      getDocument,
      setDocument,
      deleteDocument,
      collection,
      queryToObservable,
      deleteQuery,
    ],
  );
}
